import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Gavel, Pencil, Check, X, Brain, Trash2 } from 'lucide-react';
import { MatterStrategyDialog } from './MatterStrategyDialog';
import { HearingRow } from './HearingRow';
import { LinkedCauses, MatterExtraActions } from './MatterExtras';
import { ReformAlerts } from './ReformAlerts';
import { CitationsPanel } from './CitationsPanel';
import { CerebroPlusButton } from './CerebroPlusDialog';
import { CalendarCheck } from 'lucide-react';
import { JudgePicker } from './JudgePicker';
import { CourtSuggest } from './CourtSuggest';
import { MatterCourtAlerts } from './MatterCourtAlerts';
import { InstancePanel } from './InstancePanel';
import { MatterDoctrine } from './MatterDoctrine';
import { MatterRulingCheck } from './MatterRulingCheck';
import { CaseDigestPanel } from './CaseDigestPanel';
import { MatterDoctrineAlerts } from './MatterDoctrineAlerts';
import { DisciplinaryDialog } from './DisciplinaryDialog';
import { useActiveMatter } from '../../lib/activeMatter';
import { Pin, ShieldAlert } from 'lucide-react';
import { StrategyMatrixDialog } from './StrategyMatrixDialog';
import { EvaluationsDialog } from './EvaluationsDialog';
import { MatterTrends } from './MatterTrends';
import { VAL_COLOR, VAL_LABEL, OUT_LABEL } from '../../lib/evaluation';
import { MatterCourtConfirmation } from './MatterCourtConfirmation';
import { MatterVocabulary } from './MatterVocabulary';


export const MatterCard = ({ matter, clientName, onUpdated, autoOpenHearing = false }) => {
  const { api } = useAuth();
  const [editing, setEditing] = useState(false);
  const [judgeId, setJudgeId] = useState(matter.assigned_judge_id);
  const [showStrategy, setShowStrategy] = useState(false);
  const [showHearing, setShowHearing] = useState(autoOpenHearing && !!matter.next_hearing);
  const [showDisc, setShowDisc] = useState(false);
  const [active, setActive] = useActiveMatter();
  const isActive = active?.id === matter.id;

  const save = async () => {
    if (!judgeId) return toast.error('Elige un juez');
    try {
      await api.patch(`/crm/matters/${matter.id}`, { assigned_judge_id: judgeId });
      toast.success('Juez asignado y probabilidad recalculada');
      setEditing(false);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asignar juez');
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el caso «${matter.title}»?`)) return;
    try {
      await api.delete(`/crm/matters/${matter.id}`);
      toast.success('Caso eliminado');
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const ev = matter.latest_evaluation;
  const sap = matter.intake_strategy?.content?.apreciacion;
  return (
    <div data-testid={`matter-card-${matter.id}`} className={`bg-slate-900/60 rounded-xl p-4 border transition-colors ${isActive ? 'border-amber-500 ring-1 ring-amber-500/40' : 'border-slate-700'}`}>
      <button data-testid={`matter-pin-btn-${matter.id}`} onClick={() => setActive(isActive ? null : matter)} className={`mb-2 flex items-center gap-1 text-[11px] font-semibold ${isActive ? 'text-amber-300' : 'text-slate-500 hover:text-amber-300'}`}>
        <Pin className="h-3 w-3" />{isActive ? 'Causa activa' : 'Fijar como causa activa'}
      </button>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-white font-semibold truncate">{matter.title}</p>
          <p className="text-xs text-slate-400">{clientName} · <span className="capitalize">{matter.matter_type}</span> · <span className="capitalize">{matter.country}</span></p>
        </div>
        <div className="text-right shrink-0">
          <p data-testid={`matter-win-prob-${matter.id}`} className={`text-base font-bold ${ev ? VAL_COLOR[ev.valoracion] : sap ? VAL_COLOR[sap] : 'text-slate-600'}`}>
            {ev ? VAL_LABEL[ev.valoracion] : sap ? VAL_LABEL[sap] : 'Sin valorar'}
          </p>
          <p className="text-[10px] text-slate-500">{ev ? `${OUT_LABEL[ev.resultado]} · ${ev.created_at.slice(0, 10)}` : sap ? 'según estrategia aprobada' : 'valoración cualitativa'}</p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2"><StrategyMatrixDialog matter={matter} /><EvaluationsDialog matter={matter} onUpdated={onUpdated} /></div>
      <MatterTrends matter={matter} />
      <ReformAlerts matter={matter} onUpdated={onUpdated} />
      <MatterCourtAlerts matter={matter} onUpdated={onUpdated} />
      {matter.country === 'chile' && <MatterVocabulary matterId={matter.id} />}
      {matter.country === 'chile' && <MatterCourtConfirmation key={`${matter.id}-${matter.court}-${matter.court_review?.confirmed_at}`} matter={matter} onUpdated={onUpdated} />}
      {matter.country === 'chile' && <InstancePanel matter={matter} onUpdated={onUpdated} />}
      <MatterDoctrineAlerts matter={matter} onUpdated={onUpdated} />
      <MatterDoctrine matter={matter} onUpdated={onUpdated} />
      <div className="mt-2 flex flex-wrap gap-2"><MatterRulingCheck matter={matter} onUpdated={onUpdated} /><CaseDigestPanel matter={matter} onUpdated={onUpdated} /></div>
      <CitationsPanel matter={matter} onUpdated={onUpdated} />
      {!editing ? (
        <div className="mt-3 flex items-center justify-between gap-2">
          <p data-testid={`matter-judge-${matter.id}`} className="text-sm text-slate-300 flex items-center gap-2 min-w-0">
            <Gavel className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="truncate">{matter.assigned_judge_id ? <Link to={`/jueces/${matter.assigned_judge_id}`} data-testid={`judge-link-${matter.id}`} className="hover:text-amber-300 hover:underline">{matter.assigned_judge_name}</Link> : (matter.assigned_judge_name || 'Sin juez asignado')}</span>
          </p>
          <button data-testid={`matter-assign-judge-btn-${matter.id}`} onClick={() => setEditing(true)} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 shrink-0">
            <Pencil className="w-3 h-3" />{matter.assigned_judge_id ? 'Cambiar' : 'Asignar juez'}
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {matter.country === 'chile' && <CourtSuggest matter={matter} value={judgeId} onPick={setJudgeId} onUpdated={onUpdated} />}
          <JudgePicker testid={`matter-judge-picker-${matter.id}`} country={matter.country} caseType={matter.matter_type} courtLevel={matter.court_level} value={judgeId} onChange={setJudgeId} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="text-xs text-slate-400 flex items-center gap-1 px-2 py-1"><X className="w-3 h-3" />Cancelar</button>
            <button data-testid={`matter-save-judge-btn-${matter.id}`} onClick={save} className="text-xs bg-amber-500 text-slate-950 font-semibold rounded-lg px-3 py-1 flex items-center gap-1"><Check className="w-3 h-3" />Guardar</button>
          </div>
        </div>
      )}
      <HearingRow matter={matter} onUpdated={onUpdated} />
      <LinkedCauses matter={matter} onUpdated={onUpdated} />
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-slate-500 truncate">{matter.court}</p>
        <button data-testid={`matter-delete-btn-${matter.id}`} onClick={remove} className="text-slate-500 hover:text-red-400" aria-label="Eliminar caso"><Trash2 className="w-4 h-4" /></button>
      </div>
      <button
        data-testid={`matter-strategy-btn-${matter.id}`}
        onClick={() => setShowStrategy(true)}
        className="mt-3 w-full text-sm font-semibold rounded-lg py-2 flex items-center justify-center gap-2 bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 transition-colors"
      >
        <Brain className="w-4 h-4" />{matter.ai_strategy ? 'Ver estrategia de victoria' : 'Generar estrategia de victoria'}
      </button>
      {matter.next_hearing && (
        <button
          data-testid={`matter-hearing-brief-btn-${matter.id}`}
          onClick={() => setShowHearing(true)}
          className="mt-2 w-full text-sm font-semibold rounded-lg py-2 flex items-center justify-center gap-2 bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 transition-colors"
        >
          <CalendarCheck className="w-4 h-4" />Preparar audiencia
        </button>
      )}
      {matter.matter_type === 'disciplinario' && (
        <button data-testid={`matter-disciplinary-btn-${matter.id}`} onClick={() => setShowDisc(true)}
          className="mt-2 w-full text-sm font-semibold rounded-lg py-2 flex items-center justify-center gap-2 bg-red-500/10 border border-red-500/40 text-red-200 hover:bg-red-500/20 transition-colors">
          <ShieldAlert className="w-4 h-4" />{matter.disciplinary ? `Proceso disciplinario · ${matter.disciplinary.tipo_label}${matter.disciplinary.deadline ? ` · vence ${matter.disciplinary.deadline.due_date.split('-').reverse().join('-')}` : ''}` : 'Proceso disciplinario: ¿sumario u oficio?'}
        </button>
      )}
      {showDisc && <DisciplinaryDialog matter={matter} open={showDisc} onOpenChange={setShowDisc} onUpdated={onUpdated} />}
      <MatterExtraActions matter={matter} onUpdated={onUpdated} />
      <CerebroPlusButton matter={matter} />
      {showHearing && <MatterStrategyDialog mode="hearing" matter={matter} open={showHearing} onOpenChange={setShowHearing} onUpdated={onUpdated} />}
      {showStrategy && <MatterStrategyDialog matter={matter} open={showStrategy} onOpenChange={setShowStrategy} onUpdated={onUpdated} />}
    </div>
  );
};
